import { Mail, User, Phone, AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { toast } from 'react-toastify';

import { useChechkEmailMutation } from '../../services/adminAuditsApi/adminAuditApi';
import { useCreateCoOrdinatorStudentMutation } from '../../services/CoOrdinatorApi/coOrdinatorApi';
import Dropdown from '../Common/Dropdown';
import AcademicDetails from '../ReUsableComponents/AcademicDetails';

export type FormData = {
  studentName: string;
  studentEmail: string;
  mobileNumber: string;
  yearOfCommencement: string;
  yearOfPassing: string;
  department?: string;
  qualification?: string;
  gender?: 3001 | 3002 | 3003;
  collegeRollNo: string;
  academicDetails: {
    tenthPercentage?: number;
    bTechSemsPercentage?: Record<string, number>;
    interPercentage?: number;
  };
};

type AddStudentProps = {
  setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fetchStudents: () => void;
};

const AddStudent = ({ setIsDialogOpen, fetchStudents }: AddStudentProps) => {
  const [formData, setFormData] = useState<FormData>({
    studentName: '',
    studentEmail: '',
    mobileNumber: '',
    yearOfCommencement: '',
    yearOfPassing: '',
    department: '',
    qualification: '',
    gender: 3001,
    collegeRollNo: '',
    academicDetails: {}
  });
  const [createStudent, { isLoading }] = useCreateCoOrdinatorStudentMutation();
  const [checkEmail] = useChechkEmailMutation();
  // const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const isValidIndianPhone = (phone = '') => {
    const normalized = phone.replace(/\s+/g, '');
    if (/^\+91[6-9]\d{9}$/.test(normalized)) return true;
    if (/^0[6-9]\d{9}$/.test(normalized)) return true;
    if (/^[6-9]\d{9}$/.test(normalized)) return true;
    return false;
  };

  const isValidEmail = (email = '') =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isDisabled =
    !isValidEmail(formData.studentEmail) ||
    !isValidIndianPhone(formData.mobileNumber) ||
    !formData.studentName.trim() ||
    !formData.yearOfCommencement.trim() ||
    !formData.yearOfPassing.trim() ||
    formData.yearOfCommencement.trim() === '0' ||
    formData.yearOfPassing.trim() === '0' ||
    !formData.department?.trim() ||
    !formData.qualification?.trim() ||
    !formData.collegeRollNo.trim() ||
    isLoading;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'studentEmail') {
      if (value && !isValidEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'studentEmail' ? value.toLowerCase().trim() : value
    }));
  };

  const handlePhoneChange = (value: string) => {
    const formatted = value.startsWith('+') ? value : `+${value}`;
    setFormData(prev => ({ ...prev, mobileNumber: formatted }));

    if (value && !isValidIndianPhone(formatted)) {
      setPhoneError('Please enter a valid Indian mobile number');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isDisabled) return;
    try {
      const checkEmail = await checkDuplicateEmail();
      if (!checkEmail) return;
      await createStudent({
        body: {
          ...formData,
          yearOfCommencement: +formData.yearOfCommencement,
          yearOfPassing: +formData.yearOfPassing
        }
      }).unwrap();
      setIsDialogOpen(false);

      setFormData({
        studentName: '',
        studentEmail: '',
        mobileNumber: '',
        yearOfCommencement: '',
        gender: 3001,
        yearOfPassing: '',
        department: '',
        qualification: '',
        collegeRollNo: '',
        academicDetails: {}
      });
      fetchStudents();
      setIsDialogOpen(false);
      toast.success('Student created successfully');
    } catch (error) {
      const err = error as { data?: { message?: string } } | undefined;
      toast.error(err?.data?.message ?? 'Failed to add student');
      console.error(error);
    } finally {
      // setIsLoading(false);
    }
  };

  const checkDuplicateEmail = async () => {
    try {
      const emailId = formData.studentEmail;
      const response = await checkEmail({ emailId }).unwrap();
      if (response?.status && response?.isValid === true) {
        return true;
      } else {
        setEmailError('Email ID already exists. Please use a different email.');
        return false;
      }
    } catch (error) {
      console.error('error checking email:', error);
    }
  };
  const getGenderLabel = (id: number) => {
    switch (id) {
      case 3001:
        return 'Male';
      case 3002:
        return 'Female';
      case 3003:
        return 'Other';
      default:
        return '-';
    }
  };

  const handleNumChange = (e: any, semKey?: string) => {
    const { value, name } = e.target;
    if (semKey) {
      setFormData(prev => ({
        ...prev,
        academicDetails: {
          ...prev.academicDetails,
          bTechSemsPercentage: {
            ...prev.academicDetails.bTechSemsPercentage,
            [semKey]: +e.target.value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        academicDetails: {
          ...prev.academicDetails,
          [name]: +value
        }
      }));
    }
  };

  const handleAcademicClick = (action: string) => {
    if (action === 'removeSemister') {
      setFormData(prev => {
        const current = prev.academicDetails.bTechSemsPercentage || {};
        const keys = Object.keys(current);
        if (keys.length === 0) return prev;
        const lastKey = keys[keys.length - 1];
        const updated = { ...current };
        delete updated[lastKey];
        return {
          ...prev,
          academicDetails: {
            ...prev.academicDetails,
            bTechSemsPercentage: updated
          }
        };
      });
    } else if (action === 'addSemister') {
      setFormData(prev => ({
        ...prev,
        academicDetails: {
          ...prev.academicDetails,
          bTechSemsPercentage: {
            ...(prev?.academicDetails?.bTechSemsPercentage || {}),
            [`${Object?.keys(prev?.academicDetails?.bTechSemsPercentage || {}).length + 1}${addsufx(Object.keys(prev?.academicDetails?.bTechSemsPercentage || {}).length + 1)} sem`]: 0
          }
        }
      }));
    }
  };

  const addsufx = (num: number) => {
    if (num % 10 === 1 && num % 100 !== 11) {
      return 'st';
    } else if (num % 10 === 2 && num % 100 !== 12) {
      return 'nd';
    } else if (num % 10 === 3 && num % 100 !== 13) {
      return 'rd';
    } else {
      return 'th';
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className='max-h-[85%] space-y-6 overflow-y-auto p-6'
    >
      <div className='space-y-2'>
        <p className='text-muted-foreground text-sm'>
          Fill in the details to register a new student
        </p>
      </div>

      <div className='space-y-4'>
        {/* Name Input */}
        <div className='my-3 flex flex-row gap-3'>
          <div className='w-full'>
            <label
              htmlFor='studentName'
              className='text-foreground flex items-center gap-2 text-sm font-medium'
            >
              <User className='h-4 w-4 text-primary' />
              Full Name <span className='text-red-700'>*</span>
            </label>
            <input
              type='text'
              id='studentName'
              name='studentName'
              required
              value={formData.studentName}
              onChange={handleInputChange}
              placeholder="Enter student's full name"
              className='border-input bg-background w-full rounded-lg border px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
            />
          </div>

          <div className='w-full'>
            <label
              htmlFor='studentEmail'
              className='flex items-center gap-2 text-sm font-medium'
            >
              <Mail className='h-4 w-4 text-primary' />
              Email Address <span className='text-red-700'>*</span>
            </label>
            <input
              type='email'
              id='studentEmail'
              name='studentEmail'
              required
              value={formData.studentEmail}
              onChange={handleInputChange}
              placeholder='student@example.com'
              className={`w-full rounded-lg border ${
                emailError && formData.studentEmail
                  ? 'border-destructive'
                  : 'border-input'
              } px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`}
            />
            {emailError && formData.studentEmail && (
              <p className='flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {emailError}
              </p>
            )}
          </div>
        </div>
        <div className='space-y-4'>
          <div className='flex flex-row gap-3'>
            <div className='w-full'>
              <label
                htmlFor='yearOfCommencement'
                className='flex items-center gap-2 text-sm font-medium'
              >
                Year of Commencement <span className='text-red-700'>*</span>
              </label>
              <input
                type='number'
                id='yearOfCommencement'
                name='yearOfCommencement'
                required
                placeholder={'Year Of Commencement'}
                className='border-input bg-background w-full rounded-lg border px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
                onChange={handleInputChange}
                value={formData?.yearOfCommencement}
                min={0}
              />
            </div>
            <div className='w-full'>
              <label
                htmlFor='yearOfPassing'
                className='flex items-center gap-2 text-sm font-medium'
              >
                Year of Passing <span className='text-red-700'>*</span>
              </label>
              <input
                type='number'
                id='yearOfPassing'
                name='yearOfPassing'
                required
                placeholder={'Year Of Passing'}
                className='border-input bg-background w-full rounded-lg border px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
                onChange={handleInputChange}
                value={formData?.yearOfPassing}
                min={0}
              />
            </div>
          </div>
          <div className='flex flex-row gap-3'>
            <div className='w-full'>
              <label
                htmlFor='qualification'
                className='flex items-center gap-2 text-sm font-medium'
              >
                Qualification <span className='text-red-700'>*</span>
              </label>
              <input
                type='text'
                id='qualification'
                name='qualification'
                required
                placeholder={'Qualification'}
                className='border-input bg-background w-full rounded-lg border px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
                onChange={handleInputChange}
                value={formData?.qualification}
              />
            </div>
            <div className='w-full'>
              <label
                htmlFor='department'
                className='flex items-center gap-2 text-sm font-medium'
              >
                Department <span className='text-red-700'>*</span>
              </label>
              <input
                type='text'
                id='department'
                name='department'
                required
                placeholder={'Department'}
                className='border-input bg-background w-full rounded-lg border px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
                onChange={handleInputChange}
                value={formData?.department}
              />
            </div>
          </div>
          <div className='flex flex-row gap-3'>
            <div className='w-full'>
              <label
                htmlFor='gender'
                className='flex items-center gap-2 text-sm font-medium'
              >
                Gender <span className='text-red-700'>*</span>
              </label>
              <Dropdown
                key='gender'
                options={[
                  { id: 3001, label: 'Male' },
                  { id: 3002, label: 'Female' },
                  { id: 3003, label: 'Not Disclosed' }
                ]}
                getOption={(option: any) => option?.label ?? '-'}
                handleDropdownChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    gender: e.id
                  }))
                }
                value={getGenderLabel(formData?.gender || 0)}
                classes='w-full'
                optionWidth='w-full'
              />
            </div>
            <div className='w-full'>
              <label
                htmlFor='collegeRollNo'
                className='flex items-center gap-2 text-sm font-medium'
              >
                College Roll No <span className='text-red-700'>*</span>
              </label>
              <input
                type='text'
                id='collegeRollNo'
                name='collegeRollNo'
                required
                placeholder={'College Roll No'}
                className='border-input bg-background w-full rounded-lg border px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
                onChange={handleInputChange}
                value={formData?.collegeRollNo}
              />
            </div>
          </div>
        </div>

        <div className='flex flex-row gap-3'>
          {/* Phone Input */}
          <div className='w-full'>
            <label className='flex items-center gap-2 text-sm font-medium'>
              <Phone className='h-4 w-4 text-primary' />
              Mobile Number <span className='text-red-700'>*</span>
            </label>
            <PhoneInput
              country={'in'}
              countryCodeEditable={false}
              value={formData.mobileNumber}
              onChange={handlePhoneChange}
              inputClass='!w-full !rounded-lg !border !border-input !px-4 !py-3 !bg-background !text-foreground focus:!border-primary focus:!ring-2 focus:!ring-primary/20'
              containerClass='w-full'
              buttonClass='!border-input !bg-background !rounded-l-lg hover:!bg-muted'
              dropdownClass='!bg-background !text-foreground !border-border'
              specialLabel=''
              placeholder='Enter mobile number'
            />
            {phoneError && formData.mobileNumber && (
              <p className='text-destructive flex items-center gap-1 text-xs text-red-500'>
                <AlertCircle className='h-3 w-3' />
                {phoneError}
              </p>
            )}
          </div>
        </div>
      </div>
      <AcademicDetails
        formData={formData}
        onNumChange={handleNumChange}
        onClick={handleAcademicClick}
      />

      <div className='flex items-center gap-3 pt-4'>
        <button
          type='submit'
          disabled={isDisabled}
          className={`flex-1 rounded-lg px-6 py-3 font-medium text-white transition-all ${
            isDisabled
              ? 'cursor-not-allowed bg-gray-400 text-black'
              : 'bg-primary shadow-md hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]'
          }`}
        >
          {isLoading ? (
            <span className='flex items-center justify-center gap-2'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Adding Student...
            </span>
          ) : (
            'Add Student'
          )}
        </button>
      </div>
    </form>
  );
};

export default AddStudent;
